import React, { useEffect, useState } from 'react';
import { Polygon, Tooltip } from 'react-leaflet';
import L, { latLngBounds } from 'leaflet';
import 'leaflet.pattern';

import { useCtx } from '../../Context';
import { MapItem, MapRefType, Category } from '../../../types';
import './Areas.scss';
import { isMobile } from '../../../utils';
import { useTranslation } from '../../Context/Translations';

type clickProps = {
  selectMarker: (mark: MapItem) => void;
  marker: MapItem;
  map: MapRefType | null;
  bounds: L.LatLngBounds;
};

const handleClick = ({
  selectMarker,
  marker,
  map,
  bounds
}: clickProps): void => {
  selectMarker(marker);
  if (map && map.current) {
    const mapElem = map.current.leafletElement;
    let padding: [number, number] = [0, 0];

    if (isMobile) {
      // 'card' takes 60% of window height -> project coordinates to 40% window height.
      padding = [0, window.innerHeight * 0.6];
    }
    mapElem.fitBounds(bounds, { maxZoom: 18, paddingBottomRight: padding });
  }
};

const Areas = (): JSX.Element => {
  const { areas, selectMarker, selectedMarker, map, categories } = useCtx();
  const [stripe, setStripe] = useState<L.StripePattern | null>(null);
  const { currentLanguage } = useTranslation();

  useEffect(() => {
    // Add here actual check if we want the stripes in area
    // eslint-disable-next-line no-constant-condition
    if (false) {
      const stripes = new L.StripePattern({ color: '#1ea038' });
      //stripes.addTo(map.current.leafletElement);
      setStripe(stripes);
    }
  }, [map]);

  return (
    <>
      {areas.map((d: MapItem, i: number) => {
        if (d.type !== 'Area') {
          return null;
        }

        // check if current time is more than r.activeTimeStart

        if (d.activeTimeStart && d.activeTimeEnd) {
          const now = new Date();
          const start = new Date(d.activeTimeStart);
          const end = new Date(d.activeTimeEnd);
          if (start > now) return null;
          if (end < now) return null;
        }

        let cat = '';
        let catId: number | null = null;
        if (d.Categories.length !== 0) {
          cat = d.Categories[0].slug;
          catId = d.Categories[0].id;
        }

        let fillOpacity = 0.25;
        let dashArray = '0';
        if (d.styleProperties?.lineStyle === 'dotted') {
          dashArray = '1,10';
        } else if (d.styleProperties?.lineStyle === 'dashed') {
          dashArray = '10,10';
        }
        if (d.styleProperties?.fillOpacity === 0) {
          fillOpacity = 0;
        } else if (d.styleProperties?.fillOpacity === 0.25) {
          fillOpacity = 0.25;
        } else if (d.styleProperties?.fillOpacity === 0.5) {
          fillOpacity = 0.5;
        } else if (d.styleProperties?.fillOpacity === 0.75) {
          fillOpacity = 0.75;
        } else if (d.styleProperties?.fillOpacity === 1) {
          fillOpacity = 1;
        }

        //let icon = '';
        let color = '';
        const iconCat = categories.find((c: Category) => c.id === catId);
        if (iconCat) {
          //icon = iconCat.icon;
          color = iconCat.color;
        }

        const coords: L.LatLng[] = [];
        L.geoJSON(d.geo, {
          coordsToLatLng: c => {
            let ll;
            if (c[0] < c[1]) {
              ll = new L.LatLng(c[1], c[0], c[2]);
              coords.push(new L.LatLng(c[1], c[0], c[2]));
            } else {
              ll = new L.LatLng(c[0], c[1], c[2]);
              coords.push(new L.LatLng(c[0], c[1], c[2]));
            }
            return ll;
          }
        });

        const bounds = latLngBounds(coords);

        let selected = false;
        if (selectedMarker) {
          selected = selectedMarker.id === d.id;
        }

        return (
          <Polygon
            key={`area-${i}`}
            positions={d.geo.coordinates as any}
            // Add area--striped to class, if we need stripes
            className={`area area--${cat} area-${d.id} ${
              d.isInteractive ? '' : 'area--non-clickable'
            }`}
            weight={2}
            color={color ? color : '#1ea038'}
            opacity={0.4}
            dashArray={dashArray}
            fillOpacity={fillOpacity}
            interactive={d.isInteractive}
            renderer={!d.isInteractive ? L.canvas() : L.svg()}
            onClick={(): void =>
              handleClick({
                selectMarker: selectMarker,
                marker: d,
                map: map,
                bounds
              })
            }
            fillPattern={stripe}
          >
            {selected ? null : d.isInteractive ? (
              <Tooltip direction="top" className={cat}>
                {d.i18n[currentLanguage]?.title || d.title}
              </Tooltip>
            ) : null}
          </Polygon>
        );
      })}
    </>
  );
};

export default Areas;
